import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import React, { useCallback, useEffect, useState } from "react";

import {
  StyledDismissButton,
  StyledNotificationContainer,
  StyledNotificationContent,
  StyledNotificationIcon,
  StyledNotificationMessage,
  StyledNotificationText,
  StyledNotificationTitle,
} from "./styled";
import type { NotificationMessageProps } from "./types";

// Default icons for each variant using Heroicons
const DefaultIcons = {
  success: <CheckCircleIcon />,
  error: <XCircleIcon />,
  warning: <ExclamationTriangleIcon />,
  info: <InformationCircleIcon />,
};

// Close/dismiss icon using Heroicons
const CloseIcon = <XMarkIcon />;

export const NotificationMessage: React.FC<NotificationMessageProps> = ({
  children,
  variant = "info",
  size = "medium",
  title,
  showIcon = true,
  icon,
  dismissible = false,
  onDismiss,
  visible = true,
  autoHide = false,
  autoHideDelay = 5000,
  className,
  testId,
  styleProps,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(visible);
  const [isDismissing, setIsDismissing] = useState(false);

  // Handle dismiss with animation
  const handleDismiss = useCallback(() => {
    if (!isVisible) return;

    setIsDismissing(true);

    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsVisible(false);
      setIsDismissing(false);
      onDismiss?.();
    }, 200); // Match the transition duration in styled component
  }, [isVisible, onDismiss]);

  // Handle visibility changes from props
  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);

  // Handle auto-hide functionality
  useEffect(() => {
    if (autoHide && isVisible && autoHideDelay > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoHideDelay);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [autoHide, isVisible, autoHideDelay, handleDismiss]);

  // Handle keyboard events for dismiss button
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleDismiss();
      }
    },
    [handleDismiss],
  );

  // Don't render if not visible and not dismissing
  if (!isVisible && !isDismissing) {
    return null;
  }

  const iconToShow = icon ?? (showIcon ? DefaultIcons[variant] : null);

  return (
    <StyledNotificationContainer>
      <StyledNotificationMessage
        $variant={variant}
        $size={size}
        $visible={isVisible && !isDismissing}
        $styleProps={styleProps}
        className={`${className ?? ""} ${isDismissing ? "notification-dismissing" : ""}`.trim()}
        data-testid={testId}
        role="alert"
        aria-live="polite"
        {...props}
      >
        {iconToShow && (
          <StyledNotificationIcon
            $variant={variant}
            $size={size}
            $styleProps={styleProps}
            className="notification-icon"
          >
            {iconToShow}
          </StyledNotificationIcon>
        )}

        <StyledNotificationContent>
          {title && (
            <StyledNotificationTitle className="notification-title">
              {title}
            </StyledNotificationTitle>
          )}
          <StyledNotificationText className="notification-message">
            {children}
          </StyledNotificationText>
        </StyledNotificationContent>

        {dismissible && (
          <StyledDismissButton
            $variant={variant}
            $size={size}
            className="notification-dismiss"
            onClick={handleDismiss}
            onKeyDown={handleKeyDown}
            aria-label="Dismiss notification"
            type="button"
          >
            {CloseIcon}
          </StyledDismissButton>
        )}
      </StyledNotificationMessage>
    </StyledNotificationContainer>
  );
};
